import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  IconButton,
  Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import AddIcon from "@mui/icons-material/Add";
import { ReservableAsset } from "../../types/reservation";
import reservationApi from "../../services/reservation-api";
import { useTheme as useMuiTheme } from "@mui/material";
import { t } from "i18next";

interface AssetDialogProps {
  open: boolean;
  onClose: () => void;
  onSave: (asset: Partial<ReservableAsset>) => Promise<void>;
  asset?: ReservableAsset | null;
}

const AssetDialog: React.FC<AssetDialogProps> = ({
  open,
  onClose,
  onSave,
  asset,
}) => {
  const [formData, setFormData] = useState<Partial<ReservableAsset>>({
    name: "",
    type: "",
    category: "",
    description: "",
    expirationDate: "",
  });
  const [saving, setSaving] = useState(false);
  const [existingTypes, setExistingTypes] = useState<string[]>([]);
  const [existingCategories, setExistingCategories] = useState<string[]>([]);

  const muiTheme = useMuiTheme();
  const isDarkMode = muiTheme.palette.mode === "dark";
  const themeColors = {
    background: isDarkMode ? "#121212" : "#f5f8ff",
    cardBg: isDarkMode ? "#1e1e1e" : "#ffffff",
    border: isDarkMode ? "rgba(255,255,255,0.12)" : "#e2e8f0",
    borderLight: isDarkMode ? "rgba(255,255,255,0.06)" : "#f1f5f9",
    textPrimary: isDarkMode ? "#e0e0e0" : "#1e293b",
    textSecondary: isDarkMode ? "rgba(224,224,224,0.7)" : "#64748b",
    textMuted: isDarkMode ? "rgba(224,224,224,0.5)" : "#94a3b8",
    inputBg: isDarkMode ? "rgba(255,255,255,0.05)" : "#f8fafc",
    tagBg: isDarkMode ? "rgba(255,255,255,0.08)" : "#f1f5f9",
    tagBgCategory: isDarkMode ? "rgba(217,119,6,0.15)" : "#fffbeb",
    tagTextCategory: isDarkMode ? "#fbbf24" : "#d97706",
    myBookingBg: isDarkMode ? "rgba(129,140,248,0.15)" : "#eef2ff",
    myBookingBorder: isDarkMode ? "rgba(129,140,248,0.3)" : "#e0e7ff",
    myBookingText: isDarkMode ? "#a5b4fc" : "#4338ca",
    otherBookingBg: isDarkMode ? "rgba(255,255,255,0.05)" : "#f8fafc",
    hoverBg: isDarkMode ? "rgba(255,255,255,0.08)" : "#f1f5f9",
    hoverBgDanger: isDarkMode ? "rgba(239,68,68,0.15)" : "#fef2f2",
    primary: isDarkMode ? "#6db1e7" : "#6db1e7",
    primaryHover: isDarkMode ? "#6366f1" : "#4338ca",
    emptyStateBg: isDarkMode ? "rgba(255,255,255,0.05)" : "#f1f5f9",
    emptyStateBorder: isDarkMode ? "rgba(255,255,255,0.12)" : "#cbd5e1",
    legendBg: isDarkMode ? "rgba(255,255,255,0.03)" : "rgba(248,250,252,0.5)",
    addButtonBg: isDarkMode ? "#374151" : "#0f172a",
    addButtonHover: isDarkMode ? "#4b5563" : "#1e293b",
  };

  useEffect(() => {
    if (open) {
      const fetchOptions = async () => {
        try {
          const [types, categories] = await Promise.all([
            reservationApi.getDistinctTypes(),
            reservationApi.getDistinctCategories(),
          ]);
          setExistingTypes(types);
          setExistingCategories(categories);
        } catch (error) {
          console.error("Failed to fetch types/categories:", error);
        }
      };
      fetchOptions();
    }
  }, [open]);

  useEffect(() => {
    if (asset) {
      setFormData({
        name: asset.name,
        type: asset.type,
        category: asset.category || "",
        description: asset.description || "",
        expirationDate: asset.expirationDate || "",
      });
    } else {
      setFormData({
        name: "",
        type: "",
        category: "",
        description: "",
        expirationDate: "",
      });
    }
  }, [asset, open]);

  const handleChange = (field: keyof ReservableAsset, value: any) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async () => {
    setSaving(true);
    try {
      await onSave(formData);
      onClose();
    } catch (error) {
      console.error("Failed to save asset:", error);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{ sx: { borderRadius: 3 } }}
    >
      {/* Header */}
      <DialogTitle
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          bgcolor: themeColors.cardBg,
          borderBottom: `1px solid ${themeColors.border}`,
          py: 2,
        }}
      >
        <Typography variant="h6" fontWeight="bold" color="text.primary">
          {asset ? t("reservations.editAsset") : t("reservations.addAsset")}
        </Typography>
        <IconButton
          onClick={onClose}
          size="small"
          sx={{ "&:hover": { bgcolor: themeColors.hoverBg } }}
        >
          <CloseIcon
            fontSize="small"
            sx={{ color: themeColors.textSecondary }}
          />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ p: 3 }}>
        {/* Row 1: Asset Name and Asset Type */}
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: "1fr 1fr",
            gap: 2,
            mb: 2,
            mt: 3,
          }}
        >
          <Box>
            <Typography
              variant="caption"
              fontWeight="bold"
              color="text.secondary"
              sx={{
                textTransform: "uppercase",
                letterSpacing: "0.05em",
                mb: 0.5,
                display: "block",
              }}
            >
              {t("reservations.assetName")}
            </Typography>
            <TextField
              fullWidth
              size="small"
              placeholder="e.g. TM9"
              value={formData.name}
              onChange={(e) => handleChange("name", e.target.value)}
              required
              sx={{ "& .MuiOutlinedInput-root": { borderRadius: 2 } }}
            />
          </Box>
          <Box>
            <Typography
              variant="caption"
              fontWeight="bold"
              color="text.secondary"
              sx={{
                textTransform: "uppercase",
                letterSpacing: "0.05em",
                mb: 0.5,
                display: "block",
              }}
            >
              {t("reservations.assetType")}
            </Typography>
            <TextField
              fullWidth
              size="small"
              placeholder="e.g. Machinery"
              value={formData.type}
              onChange={(e) => handleChange("type", e.target.value)}
              required
              inputProps={{ list: "type-suggestions" }}
              sx={{ "& .MuiOutlinedInput-root": { borderRadius: 2 } }}
            />
            <datalist id="type-suggestions">
              {existingTypes.map((t) => (
                <option key={t} value={t} />
              ))}
            </datalist>
          </Box>
        </Box>

        {/* Row 2: Category and Expiration Date */}
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: "1fr 1fr",
            gap: 2,
            mb: 2,
          }}
        >
          <Box>
            <Typography
              variant="caption"
              fontWeight="bold"
              color="text.secondary"
              sx={{
                textTransform: "uppercase",
                letterSpacing: "0.05em",
                mb: 0.5,
                display: "block",
              }}
            >
              {t("reservations.assetCategory")}
            </Typography>
            <TextField
              fullWidth
              size="small"
              placeholder="e.g. Heavy Duty"
              value={formData.category}
              onChange={(e) => handleChange("category", e.target.value)}
              inputProps={{ list: "category-suggestions" }}
              sx={{ "& .MuiOutlinedInput-root": { borderRadius: 2 } }}
            />
            <datalist id="category-suggestions">
              {existingCategories.map((c) => (
                <option key={c} value={c} />
              ))}
            </datalist>
          </Box>
          <Box>
            <Typography
              variant="caption"
              fontWeight="bold"
              color="text.secondary"
              sx={{
                textTransform: "uppercase",
                letterSpacing: "0.05em",
                mb: 0.5,
                display: "block",
              }}
            >
              {t("reservations.assetExpirationDate")}
            </Typography>
            <TextField
              fullWidth
              size="small"
              type="date"
              value={formData.expirationDate}
              onChange={(e) => handleChange("expirationDate", e.target.value)}
              sx={{
                "& .MuiOutlinedInput-root": { borderRadius: 2 },
                "& input": { color: "#64748b" },
              }}
            />
          </Box>
        </Box>

        {/* Row 3: Description (full width) */}
        <Box>
          <Typography
            variant="caption"
            fontWeight="bold"
            color="text.secondary"
            sx={{
              textTransform: "uppercase",
              letterSpacing: "0.05em",
              mb: 0.5,
              display: "block",
            }}
          >
            {t("reservations.assetDescription")}
          </Typography>
          <TextField
            fullWidth
            size="small"
            multiline
            rows={3}
            placeholder={t("reservations.assetDescription")}
            value={formData.description}
            onChange={(e) => handleChange("description", e.target.value)}
            sx={{ "& .MuiOutlinedInput-root": { borderRadius: 2 } }}
          />
        </Box>
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 3, pt: 1, gap: 1.5 }}>
        <Button
          onClick={onClose}
          disabled={saving}
          variant="outlined"
          sx={{
            flex: 1,
            borderRadius: 2,
            py: 1.2,
            borderColor: themeColors.border,
            color: themeColors.textPrimary,
            "&:hover": {
              bgcolor: themeColors.hoverBg,
              borderColor: themeColors.borderLight,
            },
          }}
        >
          {t("common.cancel")}
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          disabled={saving || !formData.name || !formData.type}
          startIcon={<AddIcon />}
          sx={{
            flex: 1,
            borderRadius: 2,
            py: 1.2,
            bgcolor: themeColors.primary,
            "&:hover": { bgcolor: themeColors.primaryHover },
          }}
        >
          {saving
            ? t("reservations.saving")
            : asset
              ? t("reservations.saveChanges")
              : t("reservations.addAsset")}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default AssetDialog;
